import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NJButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { PLAN, UMR } from '../proposition-c/tokens';
import { Toast } from '../proposition-c/ui';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { CardRow, EmptyState, FamilyGroup, FamilyLink, HomeHeader } from './HomeCards';
import { useHome } from './hooks';
import { ProjectCard, type CardAction } from './ProjectCard';
import type { ProjectCardItem, ProjectFamily } from './types';

export const meta = {
  title: "Plan — Page d'accueil",
  description:
    "Accueil de « Création d'un projet — v2 » au format de la page d'accueil Nemo : familles de projets (original et copies), favoris explicites, nombre de plans de développement et raccourcis par carte.",
  tags: ['dashboard', 'planning', 'poc'],
  author: 'bs1253',
  status: 'draft' as const,
  createdAt: '2026-10-28',
  updatedAt: '2026-10-29',
};

const STUDIO_HEADER = 48;

const ACTION_LABEL: Record<'ouvrir' | 'hypotheses' | 'plans', string> = {
  ouvrir: 'Ouverture du projet',
  hypotheses: 'Ouverture des hypothèses',
  plans: 'Ouverture des plans de développement',
};

/** Regroupe une liste de projets en familles : projet d'origine puis ses copies. */
function toFamilies(list: ProjectCardItem[]): ProjectFamily[] {
  const byId = new Map(list.map((p) => [p.id, p]));
  return list
    .filter((p) => !p.parentId || !byId.has(p.parentId))
    .map((root) => ({ root, copies: list.filter((p) => p.parentId === root.id) }));
}

function nowLabel(): string {
  const d = new Date();
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()} à ${two(d.getHours())}:${two(d.getMinutes())}`;
}

export default function PlanPageAccueil() {
  const { data, isLoading, isError } = useHome();
  const [local, setLocal] = useState<ProjectCardItem[] | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectCardItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  useEffect(() => {
    if (data) setLocal((current) => current ?? data.projects);
  }, [data]);

  const flash = useCallback((message: string) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const projects = useMemo(() => local ?? data?.projects ?? [], [local, data]);

  const toggleFavorite = (project: ProjectCardItem) => {
    const next = !project.favorite;
    setLocal(projects.map((p) => (p.id === project.id ? { ...p, favorite: next } : p)));
    flash(next ? `« ${project.name} » ajouté à vos favoris.` : `« ${project.name} » retiré de vos favoris.`);
  };

  const duplicate = (project: ProjectCardItem) => {
    const rootId = project.parentId ?? project.id;
    const root = projects.find((p) => p.id === rootId) ?? project;
    const rank = projects.filter((p) => p.parentId === rootId).length + 1;
    const copy: ProjectCardItem = {
      ...root,
      id: `${rootId}-copie-${rank}`,
      name: `${root.name} · copie ${rank}`,
      parentId: rootId,
      copyCount: 0,
      planCount: 0,
      favorite: false,
      updatedAt: nowLabel(),
    };
    const lastOfFamily = projects.reduce(
      (acc, p, index) => (p.id === rootId || p.parentId === rootId ? index : acc),
      projects.length - 1,
    );
    const next = [...projects.map((p) => (p.id === rootId ? { ...p, copyCount: p.copyCount + 1 } : p))];
    next.splice(lastOfFamily + 1, 0, copy);
    setLocal(next);
    flash(`« ${root.name} » dupliqué : la famille compte ${rank + 1} projets.`);
  };

  const setArchived = (project: ProjectCardItem, archived: boolean) => {
    setLocal(projects.map((p) => (p.id === project.id ? { ...p, archived, favorite: archived ? false : p.favorite, updatedAt: nowLabel() } : p)));
    flash(archived
      ? `« ${project.name} » archivé — vous le retrouverez dans « Projets archivés ».`
      : `« ${project.name} » sorti de l’archive.`);
  };

  const remove = (project: ProjectCardItem) => {
    const rootId = project.parentId;
    setLocal(
      projects
        .filter((p) => p.id !== project.id && p.parentId !== project.id)
        .map((p) => (rootId && p.id === rootId ? { ...p, copyCount: Math.max(0, p.copyCount - 1) } : p)),
    );
    setPendingDelete(null);
    flash(`« ${project.name} » supprimé définitivement.`);
  };

  const handleAction = (project: ProjectCardItem) => (action: CardAction) => {
    if (action === 'dupliquer') return duplicate(project);
    if (action === 'archiver') return setArchived(project, true);
    if (action === 'restaurer') return setArchived(project, false);
    if (action === 'supprimer') return setPendingDelete(project);
    return flash(`${ACTION_LABEL[action]} · ${project.name}`);
  };

  const card = (project: ProjectCardItem, parentName?: string) => (
    <ProjectCard
      key={project.id}
      project={project}
      parentName={parentName}
      onAction={handleAction(project)}
      onToggleFavorite={() => toggleFavorite(project)}
    />
  );

  const families = (list: ProjectCardItem[]) =>
    toFamilies(list).map((family) => (
      <FamilyGroup key={family.root.id} name={family.root.name} count={1 + family.copies.length}>
        {card(family.root)}
        {family.copies.map((copy) => (
          <div key={copy.id} style={{ display: 'flex', gap: 8 }}>
            <FamilyLink />
            {card(copy, family.root.name)}
          </div>
        ))}
      </FamilyGroup>
    ));

  const active = projects.filter((p) => !p.archived);
  const archived = projects.filter((p) => p.archived);
  const mine = active.filter((p) => p.owner.id === data?.currentUser.id);
  const entity = active.filter((p) => p.owner.id !== data?.currentUser.id);
  const favorites = active.filter((p) => p.favorite);
  const newProjectToast = () => flash("Ouverture de l'assistant de création de projet");

  return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ height: `calc(100vh - ${STUDIO_HEADER}px)`, display: 'flex', flexDirection: 'column', fontFamily: UMR.font, background: UMR.white, color: UMR.textStrong }}>
        <HomeHeader />

        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 32px 40px' }}>
          <div style={{ maxWidth: 1360, margin: '0 auto' }}>
            {isLoading && <EmptyState icon="hourglass_top" title="Chargement des projets…" />}
            {isError && <EmptyState icon="error" title="Impossible de charger les projets">Réessayez dans quelques instants.</EmptyState>}

            {data && projects.length === 0 && (
              <EmptyState icon="folder_off" title="Aucun projet">
                <span>Créez un premier projet : choisissez un périmètre, les bâtiments UrbanMap y seront copiés.</span>
                <NJButton label="Nouveau projet" icon="add" onClick={newProjectToast} />
              </EmptyState>
            )}

            {data && projects.length > 0 && (
              <>
                <CardRow
                  title={`Mes projets favoris (${favorites.length})`}
                  hint="Cœur plein = projet en favori. Cliquez dessus pour le retirer de vos favoris."
                >
                  {favorites.length > 0 ? favorites.map((p) => card(p, projects.find((x) => x.id === p.parentId)?.name)) : (
                    <div style={{ fontSize: 13, lineHeight: '20px', color: UMR.textSecondary, padding: '8px 0' }}>
                      Aucun projet en favori — cliquez sur le cœur d’une carte pour en ajouter un.
                    </div>
                  )}
                </CardRow>

                <CardRow
                  title={`Mes derniers projets créés (${mine.length})`}
                  hint="Un projet dupliqué reste rattaché à son projet d’origine : ils forment une famille."
                  action={<NJButton label="Nouveau projet" onClick={newProjectToast} />}
                >
                  {families(mine)}
                </CardRow>

                <CardRow title={`Les derniers projets modifiés dans mon entité (${entity.length})`}>
                  {families(entity)}
                </CardRow>

                {archived.length > 0 && (
                  <CardRow
                    title={`Projets archivés (${archived.length})`}
                    hint="Un projet archivé reste consultable et peut être sorti de l’archive à tout moment."
                  >
                    {archived.map((p) => card(p, projects.find((x) => x.id === p.parentId)?.name))}
                  </CardRow>
                )}

                <a
                  href="#"
                  className="nj-link"
                  style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, color: PLAN.accentStrong }}
                  onClick={(event) => {
                    event.preventDefault();
                    flash('Ouverture de la liste complète des projets');
                  }}
                >
                  <span className="material-icons" aria-hidden="true">add</span>
                  Voir tous les projets
                </a>
              </>
            )}
          </div>
        </main>

        {pendingDelete && (
          <ConfirmDeleteDialog
            projectName={pendingDelete.name}
            planCount={pendingDelete.planCount}
            sharedWith={pendingDelete.members.length}
            onCancel={() => setPendingDelete(null)}
            onConfirm={() => remove(pendingDelete)}
          />
        )}
        {toast && <Toast>{toast}</Toast>}
      </div>
    </PrototypeStudioLayout>
  );
}
